/* Solar Finder (Help Me Choose). Place in /assets/solar-finder.js
   Needs assets/solar-finder-tree.js to load first (the section loads both, in order). */
(() => {
  if (customElements.get('solar-finder')) return;

  const TREE = window.SolarFinderTree;
  if (!TREE) {
    console.warn('[solar-finder] solar-finder-tree.js did not load.');
    return;
  }

  const esc = (s) =>
    String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  class SolarFinder extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.stageEl = this.querySelector('[data-sf-stage]');
      this.crumbsEl = this.querySelector('[data-sf-crumbs]');
      this.images = {};
      try {
        const raw = this.querySelector('[data-sf-images]');
        if (raw) this.images = JSON.parse(raw.textContent) || {};
      } catch (err) {
        console.warn('[solar-finder] could not read option images', err);
      }
      this.path = []; // [{ nodeId, option }]
      this.current = TREE.start;
      this.bill = '';

      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('input', (e) => {
        if (e.target.matches('[data-sf-bill]')) this.bill = e.target.value;
      });

      this.showNode(TREE.start, false);
    }

    /* ---------- events ---------- */
    onClick(e) {
      const el = e.target.closest('[data-sf-option], [data-sf-back], [data-sf-restart]');
      if (!el || !this.contains(el)) return;

      if (el.hasAttribute('data-sf-option')) this.choose(el.getAttribute('data-sf-option'));
      else if (el.hasAttribute('data-sf-back')) this.back();
      else this.restart();
    }

    choose(optionId) {
      const node = TREE.nodes[this.current];
      const option = node && node.options.find((o) => o.id === optionId);
      if (!option) return;

      this.path.push({ nodeId: this.current, option });
      if (option.next) this.showNode(option.next);
      else if (option.result) this.showResult(option.result);
    }

    back() {
      const last = this.path.pop();
      this.showNode(last ? last.nodeId : TREE.start);
    }

    restart() {
      this.path = [];
      this.showNode(TREE.start);
    }

    /* ---------- question step ---------- */
    showNode(nodeId, moveFocus = true) {
      const node = TREE.nodes[nodeId];
      if (!node) return;
      this.current = nodeId;
      this.setCrumbs(node.stage);

      if (node.type === 'bill_target' && !this.bill) this.bill = String(node.billDefault ?? '');

      const opts = node.options
        .map((o) => {
          const img = this.images[o.id];
          return `<button type="button" class="sf-opt" data-sf-option="${esc(o.id)}">
            ${img ? `<span class="sf-opt__img"><img src="${esc(img)}" alt="" loading="lazy"></span>` : ''}
            <span class="sf-opt__label">${esc(o.label)}</span>
            ${o.hint ? `<span class="sf-opt__hint">${esc(o.hint)}</span>` : ''}
          </button>`;
        })
        .join('');

      let body;
      if (node.type === 'bill_target') {
        body = `
          <div class="sf-bill">
            <label for="sf-bill-input">${esc(node.billLabel)}</label>
            <div class="sf-bill__field">
              <span>$</span>
              <input id="sf-bill-input" type="text" inputmode="decimal" autocomplete="off" data-sf-bill value="${esc(this.bill)}">
              <em>/ mo</em>
            </div>
            <p class="sf-hint">${esc(node.billHint)}</p>
          </div>
          <p class="sf-sub sf-sub--target">${esc(node.targetTitle)}</p>
          <div class="sf-options">${opts}</div>`;
      } else {
        body = `<div class="sf-options">${opts}</div>`;
      }

      this.stageEl.innerHTML = `
        <div class="sf-step ${node.type === 'bill_target' ? 'sf-step--bill' : ''}">
          <h2 class="sf-title" tabindex="-1">${esc(node.title)}</h2>
          <p class="sf-sub">${esc(node.subtitle)}</p>
          ${body}
          ${this.path.length ? this.actionsHtml(false) : ''}
        </div>`;

      this.afterRender(moveFocus);
    }

    /* ---------- result ---------- */
    chipText(entry) {
      const { nodeId, option } = entry;
      if (nodeId === 'bill_target') return `Cover ${option.label} of bill`;
      if (nodeId === 'roof_material') return `${option.label} roof`;
      return option.label;
    }

    showResult(resultId) {
      this.setCrumbs('recommendation');

      const chips = this.path
        .filter((p) => p.option.id !== 'roof') // "Metal roof" already says it
        .map((p) => `<li>${esc(this.chipText(p))}</li>`)
        .join('');

      const tpl = this.querySelector(`template[data-result="${resultId}"]`);
      const hasItems = tpl && tpl.content.querySelector('.sf-item');
      const isLowCost = resultId.startsWith('lowcost_');
      const note = isLowCost && this.dataset.lowcostNote ? `<p class="sf-note">${esc(this.dataset.lowcostNote)}</p>` : '';
      const disc = this.dataset.disclaimer ? `<p class="sf-disclaimer">${esc(this.dataset.disclaimer)}</p>` : '';

      const wrap = document.createElement('div');
      wrap.className = 'sf-result';
      wrap.innerHTML = `
        <h2 class="sf-title" tabindex="-1">${esc(this.dataset.resultTitle || 'Your Recommendation')}</h2>
        <ul class="sf-chips">${chips}</ul>
        <div data-sf-slot></div>
        ${note}${disc}
        ${this.actionsHtml(true)}`;

      const slot = wrap.querySelector('[data-sf-slot]');
      if (hasItems) slot.appendChild(tpl.content.cloneNode(true));
      else slot.innerHTML = `<p class="sf-empty">${esc(this.dataset.emptyText || '')}</p>`;

      this.stageEl.replaceChildren(wrap);
      this.afterRender(true);

      this.dispatchEvent(
        new CustomEvent('solar-finder:result', {
          bubbles: true,
          detail: { resultId, path: this.path.map((p) => p.option.id) },
        })
      );
    }

    /* ---------- helpers ---------- */
    actionsHtml(withRestart) {
      return `<div class="sf-actions">
        <button type="button" class="sf-link" data-sf-back>Back</button>
        ${withRestart ? '<button type="button" class="sf-link" data-sf-restart>Start over</button>' : ''}
      </div>`;
    }

    // Breadcrumb steps depend on the scenario picked first (see "flows" in solar-finder-tree.js)
    setCrumbs(stageKey) {
      if (!this.crumbsEl) return;
      const first = this.path[0] && this.path[0].option.id;
      const flow = TREE.flows[first] || TREE.flows.default;
      const at = Math.max(flow.indexOf(stageKey), 0);
      this.crumbsEl.innerHTML = flow
        .map((key, i) => {
          const cls = i === at ? 'is-active' : i < at ? 'is-done' : '';
          return `<li class="${cls}"${i === at ? ' aria-current="step"' : ''}>${esc(TREE.stageLabels[key] || key)}</li>`;
        })
        .join('');
    }

    afterRender(moveFocus) {
      if (!moveFocus) return;
      const h = this.stageEl.querySelector('.sf-title');
      if (h) h.focus({ preventScroll: true });
      if (this.getBoundingClientRect().top < 0) {
        const calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.scrollIntoView({ block: 'start', behavior: calm ? 'auto' : 'smooth' });
      }
    }
  }

  customElements.define('solar-finder', SolarFinder);
})();
